import React, { useMemo, useState } from 'react';
import {
  HelpArticle,
  HelpAssistantAnswer,
  answerHelpQuestion,
  getHelpArticlesForTab,
  getSectionTitle,
  getSuggestedHelpQuestions,
} from '../data/helpArticles';

type HelpAssistantProps = {
  activeTab: string;
  activeTabLabel: string;
  onOpenArticles: () => void;
  onOpenArticle: (articleId: string) => void;
  onOpenSupport: () => void;
};

type HelpChatEntry =
  | { id: number; role: 'user'; text: string }
  | { answer: HelpAssistantAnswer; id: number; role: 'assistant' };

export default function HelpAssistant({
  activeTab,
  activeTabLabel,
  onOpenArticles,
  onOpenArticle,
  onOpenSupport,
}: HelpAssistantProps) {
  const [draft, setDraft] = useState('');
  const [entries, setEntries] = useState<HelpChatEntry[]>([]);
  const suggestions = useMemo(() => getSuggestedHelpQuestions(activeTab), [activeTab]);
  const suggestedArticles = useMemo(() => getHelpArticlesForTab(activeTab).slice(0, 3), [activeTab]);
  const quickActions = useMemo(
    () => getHelpQuickActions(activeTab, suggestions, onOpenArticles, onOpenSupport),
    [activeTab, onOpenArticles, onOpenSupport, suggestions]
  );

  function askHelp(question: string) {
    const text = question.trim();
    if (!text) return;
    const answer = answerHelpQuestion(text, activeTab);
    setEntries((prev) => {
      const nextId = prev.length + 1;
      return [
        ...prev,
        { id: nextId, role: 'user', text },
        { answer, id: nextId + 1, role: 'assistant' },
      ];
    });
    setDraft('');
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    askHelp(draft);
  }

  return (
    <section className="sd-help-assistant" aria-label="Help assistant">
      <div className="sd-help-hub-hero">
        <div>
          <span>ASK HELP</span>
          <h3>What do you need help with?</h3>
          <p>Use the current {activeTabLabel} context, find a guide, or hand off to support with the right details.</p>
        </div>
        <strong>
          <span aria-hidden="true" />
          {entries.length === 0 ? 'Ready' : `${Math.floor(entries.length / 2)} asked`}
        </strong>
        <form className="sd-help-hub-search" onSubmit={handleSubmit}>
          <label>
            <span>Question</span>
            <input
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Why is my device stale?"
              type="search"
              value={draft}
            />
          </label>
          <button disabled={!draft.trim()} type="submit">Ask</button>
        </form>
      </div>

      <div className="sd-help-hub-actions" aria-label="Quick help actions">
        {quickActions.map((action) => (
          <button
            className={action.tone ? `is-${action.tone}` : ''}
            key={action.label}
            onClick={() => {
              if (action.prompt) {
                askHelp(action.prompt);
                return;
              }
              action.onClick?.();
            }}
            type="button"
          >
            <HelpHubIcon icon={action.icon} />
            <span>{action.label}</span>
            <strong>{action.detail}</strong>
          </button>
        ))}
      </div>

      <SuggestedArticleList articles={suggestedArticles} onOpenArticle={onOpenArticle} />

      <div className="sd-help-chat-log" aria-live="polite">
        {entries.length === 0 ? (
          <div className="sd-help-chat-empty">
            <strong>Ask about telemetry, alerts, reports, access, branding, MQTT, or support.</strong>
            <p>Answers stay here with source articles and practical next steps.</p>
          </div>
        ) : (
          entries.map((entry) => (
            entry.role === 'user' ? (
              <div className="sd-help-chat-message is-user" key={entry.id}>
                <p>{entry.text}</p>
              </div>
            ) : (
              <AssistantAnswerMessage
                answer={entry.answer}
                key={entry.id}
                onOpenArticle={onOpenArticle}
                onOpenSupport={onOpenSupport}
              />
            )
          ))
        )}
      </div>
    </section>
  );
}

type HelpQuickAction = {
  detail: string;
  icon: HelpHubIconName;
  label: string;
  onClick?: () => void;
  prompt?: string;
  tone?: 'support';
};

function getHelpQuickActions(
  activeTab: string,
  suggestions: string[],
  onOpenArticles: () => void,
  onOpenSupport: () => void
): HelpQuickAction[] {
  const firstPrompt = suggestions[0] || 'How do I get started?';
  const secondPrompt = suggestions[1] || 'How do I troubleshoot stale data?';
  const actionLabels: Record<string, [string, string]> = {
    alerts: ['Tune alerts', 'Test notifications'],
    dashboards: ['Build dashboard', 'Pick widget mode'],
    devices: ['Add device', 'Fix stale device'],
    export: ['Export data', 'Attach evidence'],
    live: ['Check freshness', 'Trace missing data'],
    map: ['Use floor plan', 'Locate device'],
    mqtt: ['Map gateway', 'Fix MQTT readings'],
    onboarding: ['Setup sequence', 'Workspace readiness'],
    audit: ['Review changes', 'Capture evidence'],
    overview: ['Read overview', 'Check quiet data'],
    reports: ['Choose report', 'Prepare PDF'],
    settings: ['Set branding', 'Harden security'],
    sites: ['Organize sites', 'Use site filters'],
    support: ['Open useful ticket', 'Collect evidence'],
    team: ['Invite roles', 'Review access'],
  };
  const labels = actionLabels[activeTab] || ['Quick answer', 'Troubleshoot'];

  return [
    {
      detail: firstPrompt,
      icon: 'spark',
      label: labels[0],
      prompt: firstPrompt,
    },
    {
      detail: secondPrompt,
      icon: 'pulse',
      label: labels[1],
      prompt: secondPrompt,
    },
    {
      detail: 'Current-page guides',
      icon: 'article',
      label: 'Browse articles',
      onClick: onOpenArticles,
    },
    {
      detail: 'Share issue details',
      icon: 'support',
      label: 'Open support',
      onClick: onOpenSupport,
      tone: 'support',
    },
  ];
}

function SuggestedArticleList({
  articles,
  onOpenArticle,
}: {
  articles: HelpArticle[];
  onOpenArticle: (articleId: string) => void;
}) {
  if (articles.length === 0) return null;

  return (
    <div className="sd-help-hub-suggested" aria-label="Suggested help articles">
      <div className="sd-help-hub-section-title">
        <span>Suggested for this page</span>
        <strong>{articles.length} guides</strong>
      </div>
      <div className="sd-help-hub-article-list">
        {articles.map((article) => (
          <button key={article.id} onClick={() => onOpenArticle(article.id)} type="button">
            <span>{getSectionTitle(article.section)}</span>
            <strong>{article.title}</strong>
            <small>{article.summary}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

function AssistantAnswerMessage({
  answer,
  onOpenArticle,
  onOpenSupport,
}: {
  answer: HelpAssistantAnswer;
  onOpenArticle: (articleId: string) => void;
  onOpenSupport: () => void;
}) {
  return (
    <div className="sd-help-chat-message is-assistant">
      <div className="sd-help-answer-meta">
        <span className={`is-${answer.confidence}`}>{answer.confidence} match</span>
        <small>{answer.matches.length} source{answer.matches.length === 1 ? '' : 's'}</small>
      </div>
      <p>{answer.summary}</p>
      {answer.steps.length > 0 && (
        <ol>
          {answer.steps.map((step) => <li key={step}>{step}</li>)}
        </ol>
      )}
      {answer.tips.length > 0 && (
        <div className="sd-help-answer-tips">
          {answer.tips.map((tip) => <span key={tip}>{tip}</span>)}
        </div>
      )}
      <div className="sd-help-source-list">
        {answer.matches.map((match) => (
          <button
            key={match.article.id}
            onClick={() => onOpenArticle(match.article.id)}
            type="button"
          >
            <span>{getSectionTitle(match.article.section)}</span>
            <strong>{match.article.title}</strong>
          </button>
        ))}
        {answer.confidence === 'low' && (
          <button className="is-support" onClick={onOpenSupport} type="button">
            <span>Support</span>
            <strong>Open a ticket</strong>
          </button>
        )}
      </div>
    </div>
  );
}

type HelpHubIconName = 'article' | 'pulse' | 'spark' | 'support';

function HelpHubIcon({ icon }: { icon: HelpHubIconName }) {
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
  };

  return (
    <span className="sd-help-hub-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        {icon === 'spark' && (
          <>
            <path {...common} d="M12 3l1.7 4.6L18 9.3l-4.3 1.8L12 16l-1.7-4.9L6 9.3l4.3-1.7L12 3z" />
            <path {...common} d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14z" />
          </>
        )}
        {icon === 'pulse' && <path {...common} d="M3 12h4l2-6 4 12 2-6h6" />}
        {icon === 'article' && (
          <>
            <path {...common} d="M7 3h7l4 4v14H7z" />
            <path {...common} d="M14 3v5h4M10 12h5M10 16h5" />
          </>
        )}
        {icon === 'support' && (
          <>
            <path {...common} d="M5 12a7 7 0 0 1 14 0v4a3 3 0 0 1-3 3h-2" />
            <path {...common} d="M5 12v3a2 2 0 0 0 2 2h1v-7H7a2 2 0 0 0-2 2zM19 12v3a2 2 0 0 1-2 2h-1v-7h1a2 2 0 0 1 2 2zM10 20h4" />
          </>
        )}
      </svg>
    </span>
  );
}
